<template>
<Layout  title="Competitor Price Tracker" description="Monitor competitor prices across different platforms">
  <div class="mx-auto p-6">

    <!-- Add New Tracker -->
    <div class="card mb-6">
      <div class="card-header">
        <h5 class="card-title d-flex align-items-center gap-2">
          <Search class="h-5 w-5" /> Add New Price Tracker
        </h5>
        <small class="text-muted">Enter a product URL to start tracking its price</small>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleAddUrl" class="d-flex gap-2">
          <div class="flex-grow-1">
            <label for="url" class="visually-hidden">Product URL</label>
            <input
              id="url"
              type="url"
              v-model="newUrl"
              class="form-control"
              placeholder="https://amazon.com/product-url"
              required
            />
          </div>
          <button type="submit" class="btn btn-primary d-flex align-items-center gap-2" :disabled="isScrapingUrl">
            <RefreshCw v-if="isScrapingUrl" class="h-4 w-4 animate-spin" />
            <Search v-else class="h-4 w-4" />
            <span>{{ isScrapingUrl ? "Scraping..." : "Track Price" }}</span>
          </button>
        </form>
      </div>
    </div>

    <!-- Tracked Prices Table -->
    <div class="card">
      <div class="card-header">
        <h5 class="card-title">Tracked Prices</h5>
        <small class="text-muted">Monitor price changes across different competitors</small>
      </div>
      <div class="card-body">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>Product</th>
              <th>Competitor</th>
              <th>Current Price</th>
              <th>Price Change</th>
              <th>Status</th>
              <th>Last Updated</th>
              <th >Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="price in trackedPrices" :key="price.id">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div>
                    <div class="fw-semibold">{{ price.productName }}</div>
                    <div class="text-muted small text-truncate" style="max-width:200px;">
                      {{ price.url }}
                    </div>
                  </div>
                  <button class="btn btn-sm btn-link" @click="openUrl(price.url)">
                    <ExternalLink class="h-4 w-4" />
                  </button>
                </div>
              </td>
              <td>
                <span class="badge bg-light text-dark text-capitalize">{{ price.competitor }}</span>
              </td>
              <td class="fw-semibold">{{ price.currency }} ${{ price.currentPrice.toFixed(2) }}</td>
              <td>
                <div v-if="getPriceChange(price.currentPrice, price.previousPrice)" 
                     :class="getPriceChange(price.currentPrice, price.previousPrice).percentage > 0 ? 'text-danger d-flex align-items-center gap-1' : 'text-success d-flex align-items-center gap-1'">
                  <TrendingUp v-if="getPriceChange(price.currentPrice, price.previousPrice).percentage > 0" class="h-4 w-4" />
                  <TrendingDown v-else class="h-4 w-4" />
                  <span class="small">
                    {{ getPriceChange(price.currentPrice, price.previousPrice).percentage > 0 ? '+' : '' }}
                    {{ getPriceChange(price.currentPrice, price.previousPrice).percentage }}%
                  </span>
                </div>
                <span v-else class="text-muted small">No change</span>
              </td>
              <td>
                <span class="badge text-white text-capitalize" :class="getStatusColor(price.status)">
                  {{ price.status }}
                </span>
              </td>
              <td class="small text-muted">
                {{ formatDate(price.lastUpdated) }}
              </td>
              <td >
                <div class="d-flex gap-2 ">
                  <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                          :disabled="price.status === 'updating'"
                          @click="handleRefreshPrice(price.id)">
                    <RefreshCw :class="['h-4 w-4', price.status === 'updating' ? 'animate-spin' : '']" />
                    Refresh
                  </button>
                  <button class="btn btn-outline-danger btn-sm" @click="handleRemoveTracking(price.id)">
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</Layout>
</template>

<script>
import { Search, RefreshCw, ExternalLink, Trash2, TrendingUp, TrendingDown } from "lucide-vue-next"
import Layout from '@/components/Layout.vue';
import { useToast } from 'vue-toastification'
import { getAuth, onAuthStateChanged } from "firebase/auth";
import router from "@/router";
// import axios from "axios";

export default {
  name: "PriceTracker",
  components: { Layout,Search, RefreshCw, ExternalLink, Trash2, TrendingUp, TrendingDown },
  data() {
    return {
      trackedPrices: [
        {
          id: "1",
          url: "https://amazon.com/product/123",
          productName: "Wireless Bluetooth Headphones",
          currentPrice: 79.99,
          previousPrice: 89.99,
          currency: "USD",
          lastUpdated: new Date("2024-01-20"),
          status: "active",
          competitor: "Amazon",
        },
        {
          id: "2",
          url: "https://bestbuy.com/product/456",
          productName: "Gaming Laptop 15-inch",
          currentPrice: 1299.99,
          previousPrice: 1199.99,
          currency: "USD",
          lastUpdated: new Date("2024-01-19"),
          status: "active",
          competitor: "Best Buy",
        },
      ],
      newUrl: "",
      isScrapingUrl: false,
    }
  },
    setup() {
    const toast = useToast()

    return {
      toast
    }
  },
  async mounted(){
      this.userID = this.$store.getters.getUserId;
      await this.checkAuthState();
      await this.loadCustomers();
      await this.fetchActiveTasks();

  },
  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not logged in');

      const token = await user.getIdToken(); // 🔐 always fresh
      return {
        Authorization: token
      };
    },
    async checkAuthState() {
        const auth = getAuth();
        return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
            if (user) {
            try {
                const idToken = await user.getIdToken();
                this.token = idToken;
                resolve(); // 🔥 Only resolve when token is ready
            } catch (error) {
                reject(error);
            }
            } else {
            router.push("/");
            reject(new Error("No user logged in"));
            }
        });
        });
    },
    async handleAddUrl() {
      if (!this.newUrl) return
      this.isScrapingUrl = true
      try {
        await new Promise(resolve => setTimeout(resolve, 2000))
        const hostname = new URL(this.newUrl).hostname.replace("www.", "")
        const newTrackedPrice = {
          id: Date.now().toString(),
          url: this.newUrl,
          productName: `Product from ${hostname}`,
          currentPrice: Math.floor(Math.random() * 500) + 50,
          currency: "USD",
          lastUpdated: new Date(),
          status: "active",
          competitor: hostname.split(".")[0],
        }
        this.trackedPrices.push(newTrackedPrice)
        this.newUrl = ""
        alert(`Now tracking price for ${newTrackedPrice.productName}`)
      } catch (e) {
        alert("Scraping Failed. Unable to fetch price from this URL.")
      } finally {
        this.isScrapingUrl = false
      }
    },
    handleRefreshPrice(id) {
      this.trackedPrices = this.trackedPrices.map(price =>
        price.id === id ? { ...price, status: "updating" } : price
      )
      setTimeout(() => {
        this.trackedPrices = this.trackedPrices.map(price => {
          if (price.id === id) {
            const newPrice = Math.floor(Math.random() * 500) + 50
            return {
              ...price,
              previousPrice: price.currentPrice,
              currentPrice: newPrice,
              lastUpdated: new Date(),
              status: "active",
            }
          }
          return price
        })
        alert("Latest price has been fetched successfully.")
      }, 1500)
    },
    handleRemoveTracking(id) {
      this.trackedPrices = this.trackedPrices.filter(price => price.id !== id)
      alert("Price tracking removed.")
    },
    getPriceChange(current, previous) {
      if (!previous) return null
      const change = current - previous
      const percentage = ((change / previous) * 100).toFixed(1)
      return { change, percentage: parseFloat(percentage) }
    },
    getStatusColor(status) {
      switch (status) {
        case "active": return "bg-success"
        case "error": return "bg-danger"
        case "updating": return "bg-primary"
        default: return "bg-secondary"
      }
    },
    openUrl(url) {
      window.open(url, "_blank")
    },
    formatDate(date) {
      return new Date(date).toLocaleString()
    }
  }
}
</script>
